import { RRule } from 'rrule'
import { zonedTimeToUtc } from 'date-fns-tz'
import format from 'date-fns/format'
import type { ShowSchedule } from '@/types/api'

const ET_TIMEZONE = 'America/New_York'

// Reads an instant's ET components without going through a `Date`: every date-fns-tz helper
// that hands back a `Date` (`utcToZonedTime`, and `formatInTimeZone` built on it) fills it via
// LOCAL setters, so a wall clock landing in the VIEWER's own DST gap is silently shifted an
// hour before it can be read back. `Intl` parts have no such frame to slip through.
const ET_PARTS = new Intl.DateTimeFormat('en-US', {
    timeZone: ET_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
})

function pad(value: number): string {
    return String(value).padStart(2, '0')
}

/**
 * Moves an instant into the frame rrule.js works in for a floating (no-Z) DTSTART: ET
 * wall-clock components carried in a `Date`'s UTC position. Occurrences come back in the
 * same frame, so "now" has to be reframed this way before `rule.after` can be trusted.
 */
function toEtWallClockFrame(instant: Date): Date {
    const parts: Record<string, string> = {}
    for (const { type, value } of ET_PARTS.formatToParts(instant)) {
        parts[type] = value
    }
    return new Date(
        `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}Z`
    )
}

/**
 * Turns an rrule occurrence (ET wall clock in UTC position) back into a real instant.
 *
 * The wall clock is handed to `zonedTimeToUtc` as an unambiguous string rather than as a
 * `Date` — a `Date` would be read back via LOCAL getters, which silently shifts times that
 * fall in the viewer's own DST gap before they're reinterpreted as ET.
 */
function etWallClockToInstant(occurrence: Date): Date {
    const day = `${occurrence.getUTCFullYear()}-${pad(occurrence.getUTCMonth() + 1)}-${pad(occurrence.getUTCDate())}`
    const time = `${pad(occurrence.getUTCHours())}:${pad(occurrence.getUTCMinutes())}:00`
    return zonedTimeToUtc(`${day}T${time}`, ET_TIMEZONE)
}

/** Isolated per rule so one unparseable rrule can't take out its valid siblings. */
function nextOccurrenceOf(rruleString: string, frameNow: Date): Date | null {
    try {
        return RRule.fromString(rruleString).after(frameNow, true)
    } catch {
        return null
    }
}

/** Earliest future occurrence of a show's recurring schedule, as a real UTC instant. */
export function getNextOccurrence(schedule?: ShowSchedule | null): Date | null {
    const rrules = schedule?.rrules
    if (!Array.isArray(rrules) || !rrules.length) return null

    const frameNow = toEtWallClockFrame(new Date())

    let earliest: Date | null = null
    for (const rruleString of rrules) {
        const occurrence = nextOccurrenceOf(rruleString, frameNow)
        if (!occurrence) continue

        const instant = etWallClockToInstant(occurrence)
        if (!earliest || instant < earliest) earliest = instant
    }
    return earliest
}

/**
 * "Monday, 26 October 2026 at 13:00 (your local time) on the Deep channel." — everything
 * after the component's "Next Episode:" label, which it owns so it can emphasize it alone.
 * date-fns formats in the runtime's local timezone, which is exactly what we want.
 */
export function formatNextEpisodeDetails(date: Date, channelName?: string): string {
    const day = format(date, 'EEEE, d MMMM yyyy')
    const time = format(date, 'HH:mm')
    const channelText = channelName ? ` on the ${channelName} channel.` : '.'
    return `${day} at ${time} (your local time)${channelText}`
}
